/**
 * WAF managed-DNS (Owl mDNS) — zone + zone-domain pickers and the DNS query-volume
 * diagram. Service-scoped (path: `/services/:service_id/`).
 *
 * - `GET /services/:service_id/waf/dns/zones`   → the customer's DNS zones (one per apex domain). The
 *   zone `id` (UUID) is what the diagram takes as `site_id`.
 * - `GET /services/:service_id/waf/dns/domains` → a zone's records (slim listing). `name` is the full
 *   FQDN; `ever_proxied` marks records that have edge data (use it, NOT `proxied`).
 * - `GET /services/:service_id/waf/dns/query`   → queries-per-bucket time series for one site (or org).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  serviceApiV3,
  wafKeys,
  unwrap,
  unwrapList,
  type ListResult,
} from './_shared';

/** A managed-DNS zone (one per apex domain). `id` is the `site_id` for diagrams. */
export interface WafDnsZone {
  id: string;
  domain: string;
  cdn_domain?: string;
  multi_cdn_enabled?: boolean;
  origin_shield_enabled?: boolean;
  zone_status?: number;
  ns_status?: number;
  dns_provider?: number;
}

/** A zone record (slim). `name` is the FQDN; filter the picker by `ever_proxied`. */
export interface WafDnsZoneDomain {
  id: string;
  type: string;
  name: string;
  proxied: boolean;
  ever_proxied: boolean;
  created_at?: string;
}

/** DNS query-volume diagram: shared x-axis (`labels`, unix ms) + per-series data. */
export interface WafDnsQueryDiagram {
  labels: number[];
  datasets: { label: string; data: number[] }[];
  summary: { label: string; data: number; data_percentage: number }[];
}

export interface WafDnsQueryFilter {
  /** DNS site/zone UUID (single-site). Mutually exclusive with org_id. */
  site_id?: string;
  /** Organization id (overview). */
  org_id?: string;
  /** Start unix seconds. */
  start_time: number;
  /** End unix seconds. */
  end_time: number;
  /** Bucket seconds (default 86400). */
  interval?: number;
  /** Group series by region (the "Divide" view). */
  group_regions?: boolean;
}

/** List a service's managed-DNS zones (domain picker source). */
export function useWafDnsZones(serviceId: string | undefined) {
  return useQuery<ListResult<WafDnsZone>>({
    queryKey: wafKeys.dnsZones(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      unwrapList<WafDnsZone>(
        await serviceApiV3.get(`/services/${serviceId}/waf/dns/zones`),
      ),
  });
}

/** List a zone's records (subdomain picker source; caller filters by ever_proxied). */
export function useWafDnsZoneDomains(serviceId: string | undefined, zoneId: string | undefined) {
  return useQuery<ListResult<WafDnsZoneDomain>>({
    queryKey: wafKeys.dnsZoneDomains(serviceId ?? '', zoneId ?? ''),
    enabled: !!serviceId && !!zoneId,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      unwrapList<WafDnsZoneDomain>(
        await serviceApiV3.get(`/services/${serviceId}/waf/dns/domains`, {
          params: { zone_id: zoneId },
        }),
      ),
  });
}

/** DNS query-volume diagram for a site (or org overview). */
export function useWafDnsQueryDiagram(
  serviceId: string | undefined,
  filter: WafDnsQueryFilter,
  enabled = true,
) {
  const { group_regions, ...rest } = filter;
  const params = {
    ...rest,
    // BE reads the literal string "true"; omit otherwise.
    group_regions: group_regions ? 'true' : undefined,
  };
  return useQuery<WafDnsQueryDiagram>({
    queryKey: wafKeys.dnsQueryDiagram(serviceId ?? '', params as Record<string, unknown>),
    enabled: !!serviceId && enabled,
    staleTime: 30_000,
    queryFn: async () =>
      unwrap<WafDnsQueryDiagram>(await serviceApiV3.get(`/services/${serviceId}/waf/dns/query`, { params })),
  });
}

// ---- DNS records (CRUD) --------------------------------------------------

/**
 * A DNS record (RRSet). The list/create/update response flattens the record's
 * values to `values: string[]`; weights only round-trip through the write body's
 * `detail`/`values` objects, so they aren't reflected here. `routing_policy`:
 * 0 = simple, 1 = weighted.
 */
export interface WafDnsRecord {
  id: string;
  type: string;
  name: string;
  ttl: number;
  values: string[];
  /**
   * Structured, per-type value list (SRV priority/weight/port/target, CAA
   * flags/tag, MX preference) — the fidelity `values` can't carry. Present on
   * read so the edit form can pre-fill; falls back to `values` when absent.
   */
  details?: WafDnsRecordValue[];
  proxied: boolean;
  proxiable: boolean;
  editable: boolean;
  deletable: boolean;
  cdn_cname?: string;
  cdn_status?: string;
  routing_policy?: number;
  /** SRV-only top-level selectors ("_sip", "_tcp"). */
  service?: string;
  protocol?: string;
}

/**
 * One record value. The fields are a union across record types — only the ones
 * relevant to a given type are sent, the rest stay omitted:
 * A/AAAA/CNAME/NS/TXT use `value`; MX uses `value`+`preference`; CAA uses
 * `flags`+`tag`+`value`; SRV uses `priority`+`weight`+`port`+`target`; weighted
 * routing (routing_policy=1) adds `weight`.
 */
export interface WafDnsRecordValue {
  /** A/AAAA/CNAME/NS/TXT value, MX mail-exchange host, or CAA CA value. */
  value?: string;
  /** Weighted-routing weight (routing_policy=1) and SRV weight. */
  weight?: number;
  /** MX priority (lower = higher priority). */
  preference?: number;
  /** CAA flags (typically 0). */
  flags?: number;
  /** CAA tag: "issue" | "issuewild" | "iodef". */
  tag?: string;
  /** SRV priority. */
  priority?: number;
  /** SRV port. */
  port?: number;
  /** SRV target host. */
  target?: string;
}

/** Create/update body (`providers.DNSRecordInput`). */
export interface WafDnsRecordInput {
  type: string;
  name: string;
  ttl: number;
  proxied: boolean;
  /** 0 = simple, 1 = weighted. */
  routing_policy?: number;
  values: WafDnsRecordValue[];
  /** SRV-only top-level selector, e.g. "_sip". */
  service?: string;
  /** SRV-only top-level selector, e.g. "_tcp". */
  protocol?: string;
}

/** Partial update body (`providers.DNSRecordPatch`) — only the sent fields change. */
export interface WafDnsRecordPatch {
  type?: string;
  name?: string;
  ttl?: number;
  proxied?: boolean;
  routing_policy?: number;
  values?: WafDnsRecordValue[];
}

/** A supported record type from the catalog (`GET /waf/dns/rrtypes`). */
export interface WafDnsRecordType {
  /** Standard DNS type number (A=1, AAAA=28, CNAME=5, MX=15, TXT=16…). */
  code: number;
  /** Type name, e.g. "A", "CNAME". */
  name: string;
}

export interface WafDnsRecordListFilter {
  type?: string;
  keyword?: string;
  page?: number;
  page_size?: number;
}

/** Supported DNS record types (dropdown source for the record form). */
export function useWafDnsRecordTypes(serviceId: string | undefined) {
  return useQuery<WafDnsRecordType[]>({
    queryKey: wafKeys.dnsRecordTypes(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      unwrap<{ types?: WafDnsRecordType[] }>(
        await serviceApiV3.get(`/services/${serviceId}/waf/dns/rrtypes`),
      )?.types ?? [],
  });
}

/**
 * List a zone's DNS records (paginated; optional `type`/`keyword` filter). The
 * response wraps `{ zone_id, zone_name, records, pagination }` inside the v3
 * envelope's `data`, so we read `.records`/`.pagination` (not `unwrapList`).
 */
export function useWafDnsRecords(
  serviceId: string | undefined,
  zoneId: string | undefined,
  filter: WafDnsRecordListFilter = {},
) {
  const params = { zone_id: zoneId, ...filter };
  return useQuery<ListResult<WafDnsRecord>>({
    queryKey: wafKeys.dnsRecords(serviceId ?? '', zoneId ?? '', filter as Record<string, unknown>),
    enabled: !!serviceId && !!zoneId,
    staleTime: 15_000,
    queryFn: async () => {
      const d = unwrap<{ records?: WafDnsRecord[]; pagination?: ListResult<WafDnsRecord>['pagination'] }>(
        await serviceApiV3.get(`/services/${serviceId}/waf/dns/records`, { params }),
      );
      return { items: d?.records ?? [], pagination: d?.pagination };
    },
  });
}

/** Create a DNS record (`POST /waf/dns/records`). */
export function useCreateWafDnsRecord(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafDnsRecord, Error, { zoneId: string; input: WafDnsRecordInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ zoneId, input }: { zoneId: string; input: WafDnsRecordInput }) =>
      unwrap<WafDnsRecord>(
        await serviceApiV3.post(`/services/${serviceId}/waf/dns/records`, input, {
          params: { zone_id: zoneId },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'dns-records'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Full-replace a DNS record (`PUT /waf/dns/records/:id`). */
export function useUpdateWafDnsRecord(
  serviceId: string | undefined,
  options?: UseMutationOptions<
    WafDnsRecord,
    Error,
    { zoneId: string; id: string; input: WafDnsRecordInput }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ zoneId, id, input }: { zoneId: string; id: string; input: WafDnsRecordInput }) =>
      unwrap<WafDnsRecord>(
        await serviceApiV3.put(`/services/${serviceId}/waf/dns/records/${id}`, input, {
          params: { zone_id: zoneId },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'dns-records'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Partial-update a DNS record (`PATCH /waf/dns/records/:id`) — e.g. toggle `proxied`. */
export function usePatchWafDnsRecord(
  serviceId: string | undefined,
  options?: UseMutationOptions<
    WafDnsRecord,
    Error,
    { zoneId: string; id: string; patch: WafDnsRecordPatch }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ zoneId, id, patch }: { zoneId: string; id: string; patch: WafDnsRecordPatch }) =>
      unwrap<WafDnsRecord>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/dns/records/${id}`, patch, {
          params: { zone_id: zoneId },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'dns-records'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a DNS record (`DELETE /waf/dns/records/:id`; cascade defaults true on the BE). */
export function useDeleteWafDnsRecord(
  serviceId: string | undefined,
  options?: UseMutationOptions<void, Error, { zoneId: string; id: string; cascade?: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ zoneId, id, cascade }: { zoneId: string; id: string; cascade?: boolean }) => {
      await serviceApiV3.delete(`/services/${serviceId}/waf/dns/records/${id}`, {
        params: {
          zone_id: zoneId,
          ...(cascade === undefined ? {} : { cascade: cascade ? 'true' : 'false' }),
        },
      });
    },
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'dns-records'] });
      options?.onSuccess?.(...args);
    },
  });
}
